import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, Search, X, Phone, Mail,
  MessageCircle, User, Star, Globe, Upload, Eye
} from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const CUSTOMER_TYPES      = ['Corporate','Individual','Shipper','Consignee','Agent','FF','Vendor'];
const CUSTOMER_CATEGORIES = ['Regular','VIP','High Priority'];
const CUSTOMER_STATUSES   = ['Active','Inactive','On Hold'];
const CUSTOMER_ROLES      = ['Shipper','Consignee','Notify Party','Agent','FF'];
const COMM_METHODS        = ['Email','WhatsApp','SMS','Phone'];

const EMPTY_FORM = {
  customerid: null, companyid: null, customercode: '', customername: '',
  customertype: 'Corporate', customercategory: 'Regular', customerstatus: 'Active',
  customeridnumber: '', contactperson: '', designation: '', phone: '',
  whatsappno: '', email: '', altphone: '', altemail: '', preferredcomm: 'Email',
  flatbuilding: '', street: '', areadistrict: '', cityid: '', stateid: '',
  countryid: '', zipcode: '', taxnumber: '', crnumber: '', crexpirydate: '',
  isimporterexporter: 'No', eorinumber: '', tradelicensefile: null, vatcertfile: null,
  customerrole: 'Shipper', creditlimit: '', internalremarks: '',
};

const getSessionData = () => {
  try {
    const s = localStorage.getItem('ltms_session');
    if (!s) return null;
    const d = JSON.parse(s);
    return d?.session || d;
  } catch { return null; }
};

// ── shared input/select classes ───────────────────────────────────────────
const inputCls  = (err) => `w-full px-3.5 py-2.5 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] disabled:bg-gray-100 disabled:cursor-not-allowed ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
const selectCls = (dis) => `w-full px-3.5 py-2.5 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] ${dis ? 'bg-gray-100 cursor-not-allowed text-gray-400' : ''}  border-gray-200`;
const labelCls  = 'block mb-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide';
const fieldErr  = (msg) => msg ? <p className="text-[11px] text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

// ── SectionPanel ──────────────────────────────────────────────────────────
const SectionPanel = ({ title, children }) => (
  <div className="mb-6 pb-6 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
    <div className="flex items-center gap-2 text-[11px] font-bold text-gray-500 uppercase tracking-widest
      px-3 py-2 bg-gray-50 border border-gray-200 border-l-4 rounded-r-lg mb-4"
      style={{ borderLeftColor: NAVY }}>
      {title}
    </div>
    {children}
  </div>
);

// ── FileField ─────────────────────────────────────────────────────────────
const FileField = ({ label, value, onChange, accept = '.pdf,.jpg,.jpeg,.png' }) => {
  const ref = React.useRef();
  return (
    <div className="flex-1">
      <label className={labelCls}>{label}</label>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button"
          onClick={() => ref.current.click()}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border-2 cursor-pointer
            bg-violet-50 border-violet-300 text-violet-700 transition-all duration-150
            hover:bg-violet-100 hover:border-violet-400">
          <Upload size={12} /> {value ? 'Change' : 'Upload'}
        </button>
        {value && (
          <span className="flex items-center gap-1 text-[11px] text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
            <Eye size={11} />
            {typeof value === 'string' ? value : value.name}
          </span>
        )}
        <input ref={ref} type="file" accept={accept} className="hidden"
          onChange={e => onChange(e.target.files[0] || null)} />
      </div>
      <small className="text-[10px] text-gray-400 mt-1 block">PDF / JPG / PNG</small>
    </div>
  );
};

// ── Status/Category badge helpers ─────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cls = status === 'Active'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-400'
    : status === 'Inactive'
    ? 'bg-red-50 text-red-700 border-red-400'
    : 'bg-orange-50 text-orange-700 border-orange-400';
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border-2 ${cls}`}>
      {status || 'Active'}
    </span>
  );
};

const CategoryBadge = ({ cat }) => {
  const cls = cat === 'VIP'
    ? 'bg-amber-50 text-amber-700 border-amber-400'
    : cat === 'High Priority'
    ? 'bg-rose-50 text-rose-700 border-rose-400'
    : 'bg-gray-100 text-gray-600 border-gray-300';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border-2 ${cls}`}>
      {cat === 'VIP' && <Star size={10} />}
      {cat || 'Regular'}
    </span>
  );
};

// ═════════════════════════════════════════════════════════════════════════
const Customers = () => {
  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;
  const userName  = session?.username || session?.name || 'System';

  const [customers,         setCustomers]         = useState([]);
  const [countries,         setCountries]         = useState([]);
  const [states,            setStates]            = useState([]);
  const [cities,            setCities]            = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [searchTerm,        setSearchTerm]        = useState('');
  const [filterType,        setFilterType]        = useState('all');
  const [filterStatus,      setFilterStatus]      = useState('all');
  const [showModal,         setShowModal]         = useState(false);
  const [editMode,          setEditMode]          = useState(false);
  const [submitLoading,     setSubmitLoading]     = useState(false);
  const [errors,            setErrors]            = useState({});
  const [formData,          setFormData]          = useState({ ...EMPTY_FORM, companyid: companyId || null });

  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchCountries(); fetchCustomers();
  }, [companyId, yearId]);

  useEffect(() => { filterCustomers(); }, [searchTerm, filterType, filterStatus, customers]);

  useEffect(() => {
    if (formData.countryid) fetchStates(formData.countryid);
    else { setStates([]); setCities([]); }
  }, [formData.countryid]);

  useEffect(() => {
    if (formData.stateid) fetchCities(formData.stateid);
    else setCities([]);
  }, [formData.stateid]);

  const fetchCountries = async () => {
    try {
      const res = await apiClient.get('/Countries.php', { params: { active: 1 } });
      if (res.data.success) setCountries(res.data.data || []);
    } catch (e) { console.error(e); }
  };
  const fetchStates = async (cid) => {
    try {
      const res = await apiClient.get('/States.php', { params: { countryid: cid, active: 1 } });
      if (res.data.success) setStates(res.data.data || []);
    } catch (e) { console.error(e); }
  };
  const fetchCities = async (sid) => {
    try {
      const res = await apiClient.get('/Cities.php', { params: { stateid: sid, active: 1 } });
      if (res.data.success) setCities(res.data.data || []);
    } catch (e) { console.error(e); }
  };
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Customers.php', { params: { companyid: companyId } });
      if (res.data.success) setCustomers(res.data.data || []);
      else alert('Failed to fetch customers: ' + res.data.message);
    } catch (e) {
      if (e.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error fetching customers.');
    } finally { setLoading(false); }
  };

  const filterCustomers = () => {
    let f = customers;
    if (filterType !== 'all')   f = f.filter(c => c.customertype === filterType);
    if (filterStatus !== 'all') f = f.filter(c => (c.customerstatus || 'Active') === filterStatus);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      f = f.filter(c =>
        c.customername?.toLowerCase().includes(q) ||
        c.customercode?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.contactperson?.toLowerCase().includes(q)
      );
    }
    setFilteredCustomers(f);
  };

  const set = (field, value) => setFormData(p => ({ ...p, [field]: value }));
  const handleCountryChange = (v) => setFormData(p => ({ ...p, countryid: v, stateid: '', cityid: '' }));
  const handleStateChange   = (v) => setFormData(p => ({ ...p, stateid: v, cityid: '' }));

  const validate = () => {
    const e = {};
    if (!formData.customername.trim()) e.customername = 'Customer name is required';
    if (!formData.customertype)        e.customertype = 'Customer type is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email format';
    if (formData.altemail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.altemail)) e.altemail = 'Invalid alternate email format';
    if (formData.phone && formData.phone.length < 7) e.phone = 'Phone must be at least 7 digits';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM, companyid: companyId || null });
    setStates([]); setCities([]); setEditMode(false); setErrors({});
  };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitLoading(true);
    const hasFiles = formData.tradelicensefile instanceof File || formData.vatcertfile instanceof File;
    try {
      let res;
      if (hasFiles) {
        const fd = new FormData();
        Object.entries(formData).forEach(([k, v]) => {
          if (v instanceof File) fd.append(k, v);
          else if (v !== null && v !== undefined) fd.append(k, v);
        });
        fd.set('companyid', companyId);
        fd.set('isactive', formData.customerstatus === 'Active' ? 1 : 0);
        res = await apiClient[editMode ? 'put' : 'post']('/Customers.php', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        const payload = { ...formData, companyid: companyId, isactive: formData.customerstatus === 'Active' ? 1 : 0, countryid: formData.countryid || null, stateid: formData.stateid || null, cityid: formData.cityid || null, creditlimit: formData.creditlimit || null };
        delete payload.tradelicensefile; delete payload.vatcertfile;
        res = await apiClient[editMode ? 'put' : 'post']('/Customers.php', payload);
      }
      if (res.data.success) {
        alert(!editMode && res.data.data?.customercode ? `Customer created!\nCode: ${res.data.data.customercode}` : editMode ? 'Customer updated!' : 'Customer created!');
        setShowModal(false); resetForm(); fetchCustomers();
      } else { alert('Error: ' + res.data.message); }
    } catch (e) {
      if (e.response?.status === 401) alert('Session expired.');
      else alert('Error saving customer.');
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = async (c) => {
    setFormData({
      customerid: c.customerid, companyid: c.companyid, customercode: c.customercode || '',
      customername: c.customername || '', customertype: c.customertype || 'Corporate',
      customercategory: c.customercategory || 'Regular', customerstatus: c.customerstatus || 'Active',
      customeridnumber: c.customeridnumber || '', contactperson: c.contactperson || '',
      designation: c.designation || '', phone: c.phone || '', whatsappno: c.whatsappno || '',
      email: c.email || '', altphone: c.altphone || '', altemail: c.altemail || '',
      preferredcomm: c.preferredcomm || 'Email', flatbuilding: c.flatbuilding || '',
      street: c.street || '', areadistrict: c.areadistrict || '', cityid: c.cityid || '',
      stateid: c.stateid || '', countryid: c.countryid || '', zipcode: c.zipcode || '',
      taxnumber: c.taxnumber || '', crnumber: c.crnumber || '', crexpirydate: c.crexpirydate || '',
      isimporterexporter: c.isimporterexporter || 'No', eorinumber: c.eorinumber || '',
      tradelicensefile: c.tradelicensefile || null, vatcertfile: c.vatcertfile || null,
      customerrole: c.customerrole || 'Shipper', creditlimit: c.creditlimit || '',
      internalremarks: c.internalremarks || '',
    });
    if (c.countryid) await fetchStates(c.countryid);
    if (c.stateid)   await fetchCities(c.stateid);
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Permanently delete this customer? This cannot be undone.')) return;
    try {
      const res = await apiClient.delete('/Customers.php', { data: { customerid: id } });
      if (res.data.success) setCustomers(p => p.filter(c => c.customerid !== id));
      else alert('Error: ' + res.data.message);
    } catch { alert('Error deleting customer.'); }
  };

  const filterSelectCls = `px-3 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white outline-none cursor-pointer transition-all duration-150 hover:border-[#0d1c6b] focus:border-[#0d1c6b]`;

  // ── no session ─────────────────────────────────────────────────────────
  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <User size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <User size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Customers Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage customer and logistics partner information</p>
          </div>
        </div>
        <button onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}>
          <Plus size={17} /> Add Customer
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by name, code, phone, email, contact person…"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className={filterSelectCls}>
          <option value="all">All Types</option>
          {CUSTOMER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={filterSelectCls}>
          <option value="all">All Statuses</option>
          {CUSTOMER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          {filteredCustomers.length} {filteredCustomers.length === 1 ? 'customer' : 'customers'}
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading customers…</div>
        ) : filteredCustomers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <User size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No customers found</p>
            {(searchTerm || filterType !== 'all' || filterStatus !== 'all') && (
              <button onClick={() => { setSearchTerm(''); setFilterType('all'); setFilterStatus('all'); }}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Code', 'Customer Name', 'Type / Role', 'Category', 'Contact', 'Location', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map(c => (
                  <tr key={c.customerid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                        {c.customercode}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="font-semibold text-gray-800">{c.customername}</div>
                      {c.contactperson && (
                        <div className="flex items-center gap-1 text-[12px] text-gray-500 mt-0.5">
                          <User size={11} />
                          {c.contactperson}{c.designation ? ` · ${c.designation}` : ''}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 border-2 border-blue-300
                        rounded-full text-[10px] font-bold uppercase tracking-wide">
                        {c.customertype}
                      </span>
                      {c.customerrole && c.customerrole !== c.customertype && (
                        <div className="text-[11px] text-gray-500 mt-1">{c.customerrole}</div>
                      )}
                    </td>
                    <td className="px-4 py-3.5"><CategoryBadge cat={c.customercategory} /></td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-col gap-1">
                        {c.phone      && <div className="flex items-center gap-1.5 text-[12px] text-gray-600"><Phone size={12} className="text-gray-400"/>{c.phone}</div>}
                        {c.whatsappno && <div className="flex items-center gap-1.5 text-[12px] text-gray-600"><MessageCircle size={12} className="text-gray-400"/>{c.whatsappno}</div>}
                        {c.email      && <div className="flex items-center gap-1.5 text-[12px] text-gray-600"><Mail size={12} className="text-gray-400"/>{c.email}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm">
                      {c.cityname    && <div className="text-gray-700">{c.cityname}</div>}
                      {c.statename   && <div className="text-[12px] text-gray-500">{c.statename}</div>}
                      {c.countryname && <div className="text-[11px] text-gray-400">{c.countryname}</div>}
                    </td>
                    <td className="px-4 py-3.5"><StatusBadge status={c.customerstatus || 'Active'} /></td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(c)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(c.customerid)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[860px] shadow-2xl border border-gray-200 overflow-hidden max-h-[92vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                <User size={18} />
                {editMode ? 'Edit Customer' : 'Add New Customer'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            {/* Scrollable form body */}
            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7">

              {/* ── 1. BASIC ── */}
              <SectionPanel title="Basic Information">
                <div className="mb-3">
                  <label className={labelCls}>
                    Customer Code
                    {!editMode && <span className="text-gray-400 text-[10px] font-normal ml-2 normal-case">(Auto-generated on save)</span>}
                  </label>
                  <input type="text" value={formData.customercode} disabled
                    placeholder={!editMode ? 'e.g. CUST0001' : ''}
                    className="w-full px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm bg-gray-100 cursor-not-allowed font-mono font-bold tracking-wide text-gray-500 outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className={labelCls}>Customer Name <span className="text-red-500">*</span></label>
                    <input type="text" value={formData.customername}
                      onChange={e => set('customername', e.target.value)}
                      maxLength="200" placeholder="e.g., ABC Logistics Co."
                      className={inputCls(errors.customername)} />
                    {fieldErr(errors.customername)}
                  </div>
                  <div>
                    <label className={labelCls}>Customer Type <span className="text-red-500">*</span></label>
                    <select value={formData.customertype} onChange={e => set('customertype', e.target.value)} className={selectCls(false)}>
                      {CUSTOMER_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className={labelCls}>Customer Category</label>
                    <select value={formData.customercategory} onChange={e => set('customercategory', e.target.value)} className={selectCls(false)}>
                      {CUSTOMER_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Customer Status</label>
                    <select value={formData.customerstatus} onChange={e => set('customerstatus', e.target.value)} className={selectCls(false)}>
                      {CUSTOMER_STATUSES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Customer ID Number</label>
                  <input type="text" value={formData.customeridnumber}
                    onChange={e => set('customeridnumber', e.target.value)}
                    maxLength="100" placeholder="National ID or CR Number"
                    className={inputCls(false)} />
                </div>
              </SectionPanel>

              {/* ── 2. CONTACT ── */}
              <SectionPanel title="Contact Details">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className={labelCls}>Contact Person Name</label>
                    <input type="text" value={formData.contactperson} onChange={e => set('contactperson', e.target.value)} maxLength="150" placeholder="Full name" className={inputCls(false)} />
                  </div>
                  <div>
                    <label className={labelCls}>Designation</label>
                    <input type="text" value={formData.designation} onChange={e => set('designation', e.target.value)} maxLength="100" placeholder="e.g., Manager" className={inputCls(false)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className={labelCls}><Phone size={11} className="inline mr-1"/>Phone Number</label>
                    <input type="tel" value={formData.phone} onChange={e => set('phone', e.target.value)} maxLength="50" placeholder="+966-50-1234567" className={inputCls(errors.phone)} />
                    {fieldErr(errors.phone)}
                  </div>
                  <div>
                    <label className={labelCls}><MessageCircle size={11} className="inline mr-1"/>WhatsApp Number</label>
                    <input type="tel" value={formData.whatsappno} onChange={e => set('whatsappno', e.target.value)} maxLength="50" placeholder="+966-50-1234567" className={inputCls(false)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className={labelCls}><Mail size={11} className="inline mr-1"/>Email Address</label>
                    <input type="email" value={formData.email} onChange={e => set('email', e.target.value)} maxLength="100" placeholder="contact@example.com" className={inputCls(errors.email)} />
                    {fieldErr(errors.email)}
                  </div>
                  <div>
                    <label className={labelCls}>Alternate Phone</label>
                    <input type="tel" value={formData.altphone} onChange={e => set('altphone', e.target.value)} maxLength="50" placeholder="+966-50-7654321" className={inputCls(false)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Alternate Email</label>
                    <input type="email" value={formData.altemail} onChange={e => set('altemail', e.target.value)} maxLength="100" placeholder="alt@example.com" className={inputCls(errors.altemail)} />
                    {fieldErr(errors.altemail)}
                  </div>
                  <div>
                    <label className={labelCls}>Preferred Communication</label>
                    <select value={formData.preferredcomm} onChange={e => set('preferredcomm', e.target.value)} className={selectCls(false)}>
                      {COMM_METHODS.map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
              </SectionPanel>

              {/* ── 3. ADDRESS ── */}
              <SectionPanel title="Address Information">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className={labelCls}>Flat / Building</label>
                    <input type="text" value={formData.flatbuilding} onChange={e => set('flatbuilding', e.target.value)} maxLength="100" placeholder="Flat no. / Building name" className={inputCls(false)} />
                  </div>
                  <div>
                    <label className={labelCls}>Street</label>
                    <input type="text" value={formData.street} onChange={e => set('street', e.target.value)} maxLength="200" placeholder="Street name" className={inputCls(false)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className={labelCls}>Area / District</label>
                    <input type="text" value={formData.areadistrict} onChange={e => set('areadistrict', e.target.value)} maxLength="100" placeholder="Area or district" className={inputCls(false)} />
                  </div>
                  <div>
                    <label className={labelCls}>Zip / Postal Code</label>
                    <input type="text" value={formData.zipcode} onChange={e => set('zipcode', e.target.value)} maxLength="20" placeholder="12345" className={inputCls(false)} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Country</label>
                    <select value={formData.countryid} onChange={e => handleCountryChange(e.target.value)} className={selectCls(false)}>
                      <option value="">-- Select Country --</option>
                      {countries.map(c => <option key={c.countryid} value={c.countryid}>{c.countryname}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>State</label>
                    <select value={formData.stateid} onChange={e => handleStateChange(e.target.value)} disabled={!formData.countryid} className={selectCls(!formData.countryid)}>
                      <option value="">-- Select State --</option>
                      {states.map(s => <option key={s.stateid} value={s.stateid}>{s.statename}</option>)}
                    </select>
                    {!formData.countryid && <small className="text-[10px] text-gray-400">Select country first</small>}
                  </div>
                  <div>
                    <label className={labelCls}>City</label>
                    <select value={formData.cityid} onChange={e => set('cityid', e.target.value)} disabled={!formData.stateid} className={selectCls(!formData.stateid)}>
                      <option value="">-- Select City --</option>
                      {cities.map(c => <option key={c.cityid} value={c.cityid}>{c.cityname}</option>)}
                    </select>
                    {!formData.stateid && <small className="text-[10px] text-gray-400">Select state first</small>}
                  </div>
                </div>
              </SectionPanel>

              {/* ── 4. BUSINESS & COMPLIANCE ── */}
              <SectionPanel title="Business & Compliance">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className={labelCls}>VAT / Tax Number</label>
                    <input type="text" value={formData.taxnumber} onChange={e => set('taxnumber', e.target.value)} maxLength="50" placeholder="300123456700003" className={inputCls(false)} />
                  </div>
                  <div>
                    <label className={labelCls}>CR Number</label>
                    <input type="text" value={formData.crnumber} onChange={e => set('crnumber', e.target.value)} maxLength="50" placeholder="Commercial Registration No." className={inputCls(false)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className={labelCls}>CR Expiry Date</label>
                    <input type="date" value={formData.crexpirydate} onChange={e => set('crexpirydate', e.target.value)} className={inputCls(false)} />
                  </div>
                  <div>
                    <label className={labelCls}>Is Importer / Exporter?</label>
                    <select value={formData.isimporterexporter} onChange={e => set('isimporterexporter', e.target.value)} className={selectCls(false)}>
                      <option value="No">No</option>
                      <option value="Importer">Importer</option>
                      <option value="Exporter">Exporter</option>
                      <option value="Both">Both</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className={labelCls}><Globe size={11} className="inline mr-1"/>EORI Number (International)</label>
                  <input type="text" value={formData.eorinumber} onChange={e => set('eorinumber', e.target.value)} maxLength="50" placeholder="GB123456789000" className={inputCls(false)} />
                </div>
                <div className="flex gap-6">
                  <FileField label="Trade License (PDF/JPG)" value={formData.tradelicensefile} onChange={f => set('tradelicensefile', f)} />
                  <FileField label="VAT Certificate (PDF/JPG)" value={formData.vatcertfile} onChange={f => set('vatcertfile', f)} />
                </div>
              </SectionPanel>

              {/* ── 5. OPERATIONAL ── */}
              <SectionPanel title="Operational & Logistics">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Customer Role</label>
                    <select value={formData.customerrole} onChange={e => set('customerrole', e.target.value)} className={selectCls(false)}>
                      {CUSTOMER_ROLES.map(r => <option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Credit Limit</label>
                    <input type="number" value={formData.creditlimit} onChange={e => set('creditlimit', e.target.value)} min="0" step="0.01" placeholder="0.00" className={inputCls(false) + ' text-right'} />
                  </div>
                </div>
              </SectionPanel>

              {/* ── 6. REMARKS ── */}
              <SectionPanel title="Internal Remarks">
                <label className={labelCls}>Internal Remarks</label>
                <textarea value={formData.internalremarks} onChange={e => set('internalremarks', e.target.value)}
                  rows={3} maxLength={1000} placeholder="Internal notes (not visible to customer)"
                  className="w-full px-3.5 py-2.5 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] resize-y min-h-[72px] font-[inherit]" />
              </SectionPanel>

              {/* Audit row */}
              {editMode && (
                <div className="flex gap-6 text-xs text-gray-400 px-1 mb-4">
                  <span>Last Updated By: <strong className="text-gray-600">{userName}</strong></span>
                  <span>Today: <strong className="text-gray-600">{new Date().toLocaleDateString()}</strong></span>
                </div>
              )}

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-6 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving…' : (editMode ? 'Update Customer' : 'Create Customer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default Customers;